import { RuleType, parser } from 'markdown-to-jsx';

/** Nodes holding their text directly, under the property named here. */
const LITERAL = {
  [RuleType.text]: 'text',
  [RuleType.codeInline]: 'text',
  [RuleType.codeBlock]: 'text',
  [RuleType.image]: 'alt'
};

/** Nodes rendering as a break between words rather than as text of their own. */
const SPACER = new Set([RuleType.breakLine, RuleType.breakThematic, RuleType.htmlSelfClosing]);

/** Nodes whose text lives in `children`. `htmlBlock` also has `text`, which repeats them. */
const INLINE = new Set([RuleType.link, RuleType.textFormatted, RuleType.htmlBlock]);

/** The same, but block-level, so what follows starts a new word. */
const BLOCK = new Set([RuleType.blockQuote, RuleType.heading, RuleType.paragraph]);

const LIST = new Set([RuleType.orderedList, RuleType.unorderedList]);

/**
 * Tags whose body never rendered as words. Excluded by tag because the parser puts a `<script>` body
 * in `text` standalone but in `children` mid-sentence, so walking children only drops the first.
 */
const NON_CONTENT_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template'
]);

/**
 * Flattens an AST node, or a list of them, to its text.
 *
 * Siblings join with no separator, because adjacent inline nodes are adjacent words: `**a**b**c**` is
 * the single word `abc`. Block nodes add a trailing space instead.
 *
 * Anything unnamed above contributes nothing, which is what drops a comment, frontmatter, a footnote
 * marker and a reference definition — none of which the shopper read.
 */
const flatten = (node) => {
  if (Array.isArray(node)) {
    return node.map(flatten).join('');
  }

  if (!node || typeof node !== 'object') {
    return '';
  }

  const literal = LITERAL[node.type];
  if (literal) {
    return node[literal] || '';
  }

  if (SPACER.has(node.type)) {
    return ' ';
  }

  if (node.type === RuleType.htmlBlock && NON_CONTENT_TAGS.has(node.tag)) {
    return ' ';
  }

  if (INLINE.has(node.type)) {
    return flatten(node.children);
  }

  if (BLOCK.has(node.type)) {
    return `${flatten(node.children)} `;
  }

  if (LIST.has(node.type)) {
    return (node.items || []).map((item) => `${flatten(item)} `).join('');
  }

  if (node.type === RuleType.table) {
    return [node.header, ...(node.cells || [])]
      .map((row) => (Array.isArray(row) ? row : []).map((cell) => `${flatten(cell)} `).join(''))
      .join('');
  }

  return '';
};

/**
 * Reduces a markdown string to the plain text it renders as.
 *
 * The `suggestedQuestions` API returns question text as markdown, which Magic Apron must not receive.
 * Using `markdown-to-jsx`'s parser — the one `MagicMarkdown` renders with — means the assistant is
 * asked what the shopper read, escapes, entities and near-markdown included.
 *
 * A source the parser rejects falls back to itself: a question with delimiters beats no question.
 *
 * Returns `''` whenever nothing readable is left — a non-string, a blank string, or markup that
 * reduces to nothing, such as a lone bullet or an empty tag — which callers read as "no question".
 *
 * @param {string} markdown - Markdown source string.
 * @returns {string} The rendered text, whitespace collapsed and trimmed.
 */
export const stripMarkdown = (markdown) => {
  if (typeof markdown !== 'string' || !markdown) {
    return '';
  }

  let text;
  try {
    text = flatten(parser(markdown));
  } catch {
    text = markdown;
  }

  return text.replace(/\s+/g, ' ').trim();
};
